import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRinging, BellSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Iter209dx — Widget scadenze in arrivo.
 *
 * Mostra una card compatta in cima alla dashboard Pro con il numero
 * di scadenze nei prossimi 15 giorni. Al click espande la lista con
 * descrizione, immobile e data; click su un item porta alla pagina
 * dell'immobile.
 *
 * Si auto-nasconde se count === 0 (nessun rumore inutile).
 */

const CATEGORY_LABELS = {
  scadenza_documenti_personali: "Documenti personali",
  scadenza_mandato_vendita: "Mandato di vendita",
  scadenza_compleanno: "Compleanno",
  scadenza_incarico: "Incarico",
  scadenza_vincoli_vendita: "Vincolo alla vendita",
};

const MONTHS_IT_SHORT = [
  "gen", "feb", "mar", "apr", "mag", "giu",
  "lug", "ago", "set", "ott", "nov", "dic",
];

function formatDateItShort(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const month = MONTHS_IT_SHORT[parseInt(m[2], 10) - 1] || m[2];
  const d = parseInt(m[3], 10);
  return `${d} ${month} ${m[1]}`;
}

/**
 * Iter209dzi — Compone la riga "titolo · indirizzo" senza duplicati.
 *
 * Scenario tipico: il pro inserisce come titolo lo stesso indirizzo che
 * poi finisce in `property_address` arricchito di città (es. title
 * `"via Milano 104"`, address `"via Milano 104, Milano"`). Prima
 * mostravamo `"via Milano 104 · via Milano 104, Milano"`. Ora:
 *  - se address coincide o inizia con title + boundary (spazio/virgola/
 *    fine stringa), mostriamo SOLO address (già incluso tutto);
 *  - se solo uno dei due è presente, mostriamo quello;
 *  - altrimenti mostriamo `title · address`.
 *
 * Iter209dzm — Sostituito `includes` con equality + startsWith seguito
 * da un word boundary così titoli come "Via Roma" NON vengono
 * silenziosamente nascosti dentro address come "Via Romana 5, Milano".
 */
function formatTitleAndAddress(title, address) {
  const t = (title || "").trim();
  const a = (address || "").trim();
  if (!t && !a) return "";
  if (!a) return t;
  if (!t) return a;
  const tl = t.toLowerCase();
  const al = a.toLowerCase();
  if (al === tl) return a;
  if (al.startsWith(tl)) {
    const nextChar = al.charAt(tl.length);
    // Word boundary: fine stringa oppure spazio/virgola/punto/trattino.
    if (nextChar === "" || /[\s,.\-;·]/.test(nextChar)) return a;
  }
  return `${t} · ${a}`;
}

export { formatTitleAndAddress };

export default function UpcomingDeadlinesWidget() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/properties/upcoming-deadlines?days=15")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  const mute = async (e, it) => {
    e.stopPropagation();
    try {
      await api.post(`/properties/${it.property_id}/scadenze/${it.scadenza_id}/mute`);
      setData((d) => {
        const rest = (d.items || []).filter((x) => x.scadenza_id !== it.scadenza_id);
        return { ...d, items: rest, count: rest.length };
      });
    } catch {
      toast.error("Impossibile silenziare la scadenza");
    }
  };

  if (!data || !data.count) return null;

  const items = data.items || [];
  const first = items[0];
  const subtitle = items.length === 1
    ? "scadenza in arrivo nei prossimi 15 giorni"
    : "scadenze in arrivo nei prossimi 15 giorni";

  return (
    <div
      className="rounded-xl border border-red-200 bg-gradient-to-r from-red-50 to-amber-50 p-4 mb-5 shadow-sm"
      data-testid="dashboard-deadlines-widget"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md shadow-red-500/40 shrink-0">
          <BellRinging size={22} weight="fill" className="vibrate-bell" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold text-slate-900 leading-tight">
            {data.count} {subtitle}
          </div>
          {first && !expanded && (
            <div className="text-sm text-slate-700 mt-0.5 truncate">
              <span className="font-medium">{CATEGORY_LABELS[first.categoria] || "Scadenza"}</span>
              {" — "}
              {first.descrizione}
              {" ("}
              <span className="text-red-600 font-semibold">{formatDateItShort(first.data_scadenza)}</span>
              {")"}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 text-sm font-semibold text-purple-700 hover:text-purple-900 underline"
          data-testid="dashboard-deadlines-toggle"
        >
          {expanded ? "Chiudi" : "Vedi tutte"}
        </button>
      </div>
      {expanded && (
        <ul
          className="mt-3 divide-y divide-red-100 border-t border-red-100"
          data-testid="dashboard-deadlines-list"
        >
          {items.map((it) => {
            const past = it.days_remaining < 0;
            return (
              <li
                key={it.scadenza_id || `${it.property_id}-${it.data_scadenza}-${it.descrizione}`}
                className="py-2 flex items-center gap-3 cursor-pointer hover:bg-red-100/40 rounded px-2 -mx-2"
                onClick={() => nav(`/properties/${it.property_id}`)}
                data-testid={`deadline-item-${it.property_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {CATEGORY_LABELS[it.categoria] || "Scadenza"} — {it.descrizione}
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {formatTitleAndAddress(it.property_title, it.property_address)}
                  </div>
                </div>
                {it.scadenza_id && (
                  <button
                    type="button"
                    onClick={(e) => mute(e, it)}
                    className="shrink-0 p-1.5 rounded-full text-slate-500 hover:text-red-700 hover:bg-red-100 transition-colors"
                    title="Silenzia questa scadenza"
                    aria-label="Silenzia questa scadenza"
                    data-testid={`deadline-mute-${it.scadenza_id}`}
                  >
                    <BellSlash size={16} />
                  </button>
                )}
                <div className={`text-xs font-semibold whitespace-nowrap w-36 text-right ${past ? "text-red-700" : "text-slate-800"}`}>
                  {past ? "scaduta il " : "scade il "}
                  {formatDateItShort(it.data_scadenza)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
